import { EnumOAuthAuthorizationStatus } from '../enums/oauth-authorization-status.enum';
import { EnumOAuthInteractionMode } from '../enums/oauth-interaction-mode.enum';

/* eslint-disable @typescript-eslint/naming-convention */
export interface IConnectionRaw {
  id: string;
  connector_slug: string;
  connector_name: string;
  alias: string | null;
  status: string;
  enabled: boolean;
  auth_method: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface IAuthorizationRaw {
  connection: IConnectionRaw;
  authorization_url: string;
  authorization_method: string;
  authorization_token: string;
  expires_at: string;
  interaction_mode: EnumOAuthInteractionMode;
}

export interface IAuthorizationStatusRaw {
  connection_id: string;
  status: EnumOAuthAuthorizationStatus;
  terminal: boolean;
  connector_error: string | null;
  expires_at: string | null;
  updated_at: string | null;
}

export interface ICreateConnectionRequestRaw {
  alias?: string;
  allow_multiple: boolean;
  return_path?: string;
  interaction_mode?: EnumOAuthInteractionMode;
}

export interface IUpdateConnectionRequestRaw {
  alias?: string;
  enabled?: boolean;
}

export interface IReconnectRequestRaw {
  return_path?: string;
  interaction_mode?: EnumOAuthInteractionMode;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface IConnection {
  id: string;
  connectorSlug: string;
  connectorName: string;
  alias: string | null;
  status: string;
  enabled: boolean;
  authMethod: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface IAuthorization {
  connection: IConnection;
  authorizationUrl: string;
  authorizationMethod: string;
  authorizationToken: string;
  expiresAt: string;
  interactionMode: EnumOAuthInteractionMode;
}

export interface IAuthorizationStatus {
  connectionId: string;
  status: EnumOAuthAuthorizationStatus;
  terminal: boolean;
  connectorError: string | null;
  expiresAt: string | null;
  updatedAt: string | null;
}

export interface ICreateConnectionRequest {
  alias?: string;
  allowMultiple?: boolean;
  returnPath?: string;
  interactionMode?: EnumOAuthInteractionMode;
}

export interface IReconnectRequest {
  returnPath?: string;
  interactionMode?: EnumOAuthInteractionMode;
}

export interface IUpdateConnectionRequest {
  alias?: string;
  enabled?: boolean;
}

export const toConnection = (raw: IConnectionRaw): IConnection => ({
  id: raw.id,
  connectorSlug: raw.connector_slug,
  connectorName: raw.connector_name,
  alias: raw.alias,
  status: raw.status,
  enabled: raw.enabled,
  authMethod: raw.auth_method,
  createdAt: raw.created_at,
  updatedAt: raw.updated_at
});

export const toAuthorization = (raw: IAuthorizationRaw): IAuthorization => ({
  connection: toConnection(raw.connection),
  authorizationUrl: raw.authorization_url,
  authorizationMethod: raw.authorization_method,
  authorizationToken: raw.authorization_token,
  expiresAt: raw.expires_at,
  interactionMode: raw.interaction_mode
});

export const toAuthorizationStatus = (
  raw: IAuthorizationStatusRaw
): IAuthorizationStatus => ({
  connectionId: raw.connection_id,
  status: raw.status,
  terminal: raw.terminal,
  connectorError: raw.connector_error,
  expiresAt: raw.expires_at,
  updatedAt: raw.updated_at
});

export const toCreateConnectionRequestRaw = (
  request: ICreateConnectionRequest
): ICreateConnectionRequestRaw => ({
  alias: request.alias,
  allow_multiple: request.allowMultiple ?? false,
  return_path: request.returnPath,
  interaction_mode: request.interactionMode
});

export const toReconnectRequestRaw = (
  request: IReconnectRequest
): IReconnectRequestRaw => ({
  return_path: request.returnPath,
  interaction_mode: request.interactionMode
});

export const toUpdateConnectionRequestRaw = (
  request: IUpdateConnectionRequest
): IUpdateConnectionRequestRaw => ({
  alias: request.alias,
  enabled: request.enabled
});
